import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Sparkles, Star, Shield, Users, TrendingUp, Heart, ChevronRight, ArrowDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import EmailAuthForm from "@/components/EmailAuthForm";
import { isNativeApp } from "@/lib/platform";
import { isNativeAppleAvailable, isNativeGoogleAvailable } from "@/lib/social-auth";
import MusicToggle from "@/components/MusicToggle";

function useScrollReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("revealed"); obs.unobserve(el); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useScrollReveal();
  return (
    <div ref={ref} className={`scroll-reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function LandingPage() {
  const { user, login, loginWithGoogleNative, loginWithGoogleEmergent, loginWithAppleNative, loading } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [providerBusy, setProviderBusy] = useState(false);
  const [providerError, setProviderError] = useState("");
  const native = isNativeApp();
  const showGoogle = true;
  const showApple = isNativeAppleAvailable();

  const signInWith = async (fn) => {
    setProviderError("");
    setProviderBusy(true);
    const res = await fn();
    setProviderBusy(false);
    if (res.success) {
      setAuthOpen(false);
      navigate("/dashboard");
    } else if (!res.cancelled) {
      setProviderError(res.error);
    }
  };

  const handleGoogle = () => {
    if (!native) return login();
    return signInWith(isNativeGoogleAvailable() ? loginWithGoogleNative : loginWithGoogleEmergent);
  };
  const handleApple = () => signInWith(loginWithAppleNative);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  // On the native mobile app, open the sign-in sheet immediately.
  useEffect(() => {
    if (native && !loading && !user) setAuthOpen(true);
  }, [native, loading, user]);

  const startAuth = () => setAuthOpen(true);

  return (
    <div className="min-h-screen overflow-hidden" data-testid="landing-page">
      {/* Ambient background with animated orbs */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#020617]" />
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
      </div>

      {/* Nav */}
      <nav className="flex items-center justify-between px-6 sm:px-12 py-5 relative z-10">
        <div className="flex items-center gap-2.5">
          <Sparkles className="h-6 w-6 text-amber-400 animate-[spin_8s_linear_infinite]" />
          <span className="font-cinzel text-xl font-bold tracking-wider text-amber-400">Tara Kundli</span>
        </div>
        <div className="flex items-center gap-1">
          <MusicToggle />
          <Button onClick={startAuth} variant="outline" size="sm"
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs sm:text-sm gap-2 backdrop-blur-sm"
            data-testid="nav-sign-in">
            Sign In
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="flex flex-col items-center text-center px-6 pt-16 sm:pt-28 pb-16 relative z-10">
        {/* Animated concentric rings */}
        <div className="relative mb-10">
          <div className="w-24 h-24 rounded-full border border-amber-500/10 flex items-center justify-center animate-[spin_20s_linear_infinite]">
            <div className="w-16 h-16 rounded-full border border-amber-500/20 flex items-center justify-center animate-[spin_12s_linear_infinite_reverse]">
              <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-amber-400" />
              </div>
            </div>
          </div>
          <div className="absolute inset-0 w-24 h-24 rounded-full bg-amber-500/8 blur-2xl animate-pulse" />
        </div>

        <h1 className="font-cinzel text-4xl sm:text-6xl lg:text-8xl font-bold tracking-wider text-white mb-6 leading-[1.1] max-w-5xl hero-text-appear">
          Your Stars,{" "}
          <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 bg-clip-text text-transparent">
            Decoded
          </span>
        </h1>

        <p className="text-slate-400 text-base sm:text-lg max-w-2xl mb-12 leading-relaxed hero-text-appear" style={{ animationDelay: "0.2s" }}>
          Precise Vedic birth charts powered by Swiss Ephemeris. Personal life readings
          with deep Dasha analysis, planetary aspects, and transit predictions.
        </p>

        <div className="hero-text-appear" style={{ animationDelay: "0.4s", position: "relative", zIndex: 10 }}>
          <Button onClick={startAuth} size="lg"
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold text-base sm:text-lg px-10 py-7 rounded-full gap-3 group shadow-[0_0_40px_rgba(245,158,11,0.2)] hover:shadow-[0_0_60px_rgba(245,158,11,0.3)] transition-all duration-500"
            data-testid="hero-sign-in">
            <Sparkles className="h-5 w-5" />
            Get Started
            <ChevronRight className="h-5 w-5 group-hover:translate-x-1.5 transition-transform duration-300" />
          </Button>
        </div>

        <p className="text-slate-600 text-xs mt-5 hero-text-appear" style={{ animationDelay: "0.6s" }}>Free forever. No credit card required.</p>

        {/* Scroll indicator */}
        <div className="mt-16 flex flex-col items-center gap-2 hero-text-appear" style={{ animationDelay: "0.8s" }}>
          <span className="text-slate-600 text-[10px] tracking-widest uppercase">Discover</span>
          <ArrowDown className="h-4 w-4 text-slate-600 animate-bounce" />
        </div>
      </section>

      {/* Stats counter */}
      <Reveal>
        <section className="px-6 py-16 max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-8 text-center">
            {[
              { value: "50K+", label: "Charts Generated" },
              { value: "99.9%", label: "Calculation Accuracy" },
              { value: "34", label: "Languages Supported" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 150}>
                <div>
                  <p className="font-cinzel text-3xl sm:text-4xl font-bold bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent">{s.value}</p>
                  <p className="text-slate-500 text-xs mt-2">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </Reveal>

      {/* Features */}
      <section className="px-6 sm:px-12 py-20 max-w-6xl mx-auto">
        <Reveal>
          <h2 className="font-cinzel text-2xl sm:text-4xl font-bold text-center text-white tracking-wide mb-3">
            Ancient Wisdom, Modern Precision
          </h2>
          <p className="text-slate-500 text-center text-sm max-w-lg mx-auto mb-16">
            Every reading is grounded in actual planetary positions, not generic horoscopes.
          </p>
        </Reveal>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Star, color: "text-amber-400", bg: "bg-amber-500/10", border: "hover:border-amber-500/30", title: "Precise Kundli Charts", desc: "Swiss Ephemeris calculations with Lahiri ayanamsa. North Indian diamond charts with degrees, retrograde markers, exaltation, and Vargottama." },
            { icon: TrendingUp, color: "text-violet-400", bg: "bg-violet-500/10", border: "hover:border-violet-500/30", title: "Deep Dasha Analysis", desc: "Maha Dasha and Antar Dasha periods to the exact day. Expandable sub-periods with active period highlighting." },
            { icon: Shield, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "hover:border-emerald-500/30", title: "Personalized Remedies", desc: "Gemstones, mantras, donations, and pujas tailored to your exact chart. Specific carat, finger, day, and Sanskrit mantra details." },
            { icon: Heart, color: "text-pink-400", bg: "bg-pink-500/10", border: "hover:border-pink-500/30", title: "Life Predictions", desc: "Deep analysis using Drishti, transits, Yogas, and house lords. Ask about love, career, marriage — get chart-grounded answers." },
            { icon: Users, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "hover:border-cyan-500/30", title: "Marriage Compatibility", desc: "Ashtakoot Gun Milan, Manglik analysis, Moon sign compatibility, and remedies for any doshas found." },
            { icon: Sparkles, color: "text-amber-400", bg: "bg-amber-500/10", border: "hover:border-amber-500/30", title: "34 Languages", desc: "Hindi, Tamil, Telugu, Bengali, Sanskrit, Spanish, French, Arabic, Chinese, Japanese, Korean, and many more." },
          ].map((f, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className={`group glass rounded-2xl p-7 ${f.border} transition-all duration-500 hover:translate-y-[-4px] hover:shadow-xl`}>
                <div className={`w-11 h-11 rounded-xl ${f.bg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-500`}>
                  <f.icon className={`h-5 w-5 ${f.color}`} />
                </div>
                <h3 className="text-white font-semibold text-base mb-2">{f.title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Visual divider with quote */}
      <Reveal>
        <section className="px-6 py-24 text-center max-w-3xl mx-auto">
          <div className="w-12 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent mx-auto mb-8" />
          <blockquote className="font-cinzel text-lg sm:text-2xl text-slate-300 leading-relaxed italic">
            "Jyotisham Vedanam Chakshuh"
          </blockquote>
          <p className="text-slate-500 text-xs mt-3">Astrology is the eye of the Vedas — Vedanga Jyotisha</p>
          <div className="w-12 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent mx-auto mt-8" />
        </section>
      </Reveal>

      {/* CTA */}
      <Reveal>
        <section className="px-6 py-24 text-center relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-500/[0.03] to-transparent" />
          <h2 className="font-cinzel text-3xl sm:text-4xl font-bold text-white tracking-wide mb-4 relative">
            Begin Your Cosmic Journey
          </h2>
          <p className="text-slate-500 text-sm mb-10 max-w-md mx-auto relative">
            One-click Google sign in. Your birth chart in seconds. Deep, personalized insights instantly.
          </p>
          <Button onClick={startAuth} size="lg"
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold text-base px-12 py-7 rounded-full gap-2 shadow-[0_0_40px_rgba(245,158,11,0.15)] hover:shadow-[0_0_60px_rgba(245,158,11,0.25)] transition-all duration-500 relative z-10"
            data-testid="cta-sign-in">
            Get Started Free
            <ChevronRight className="h-4 w-4" />
          </Button>
        </section>
      </Reveal>

      {/* Auth Sheet */}
      {authOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setAuthOpen(false)}
          data-testid="auth-modal-overlay"
        >
          <div
            className="relative w-full sm:max-w-md max-h-full overflow-y-auto overscroll-contain bg-slate-900 border-t sm:border border-white/10 sm:rounded-2xl p-6 pb-[max(2rem,env(safe-area-inset-bottom))] shadow-[0_-20px_60px_rgba(245,158,11,0.08)]"
            onClick={(e) => e.stopPropagation()}
            data-testid="auth-modal"
          >
            <button
              onClick={() => setAuthOpen(false)}
              className="absolute top-3 right-3 text-slate-500 hover:text-amber-400 p-1.5 rounded-full hover:bg-white/5"
              data-testid="auth-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex flex-col items-center mb-5">
              <Sparkles className="h-7 w-7 text-amber-400 mb-2" />
              <h2 className="font-cinzel text-xl font-bold text-amber-400 tracking-wider">Tara Kundli</h2>
              <p className="text-slate-500 text-xs mt-1">Sign in to read your stars</p>
            </div>

            <EmailAuthForm onSuccess={() => { setAuthOpen(false); navigate("/dashboard"); }} />

            {(showGoogle || showApple) && (
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] tracking-widest uppercase text-slate-500">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            )}

            {showApple && (
              <Button
                onClick={handleApple}
                disabled={providerBusy}
                className="w-full bg-white hover:bg-slate-100 text-black font-medium rounded-full py-5 gap-2 mb-3"
                data-testid="apple-login-button"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.37 12.6c-.02-2.3 1.88-3.4 1.97-3.46-1.07-1.57-2.74-1.78-3.33-1.8-1.42-.14-2.77.83-3.49.83-.72 0-1.83-.81-3.01-.79-1.55.02-2.98.9-3.78 2.29-1.61 2.79-.41 6.93 1.16 9.2.77 1.11 1.68 2.36 2.88 2.31 1.16-.05 1.59-.75 2.99-.75 1.4 0 1.79.75 3.01.72 1.24-.02 2.03-1.13 2.79-2.25.88-1.29 1.24-2.53 1.26-2.6-.03-.01-2.42-.93-2.45-3.7zM14.09 5.84c.64-.77 1.07-1.85.95-2.92-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.77-.97 2.82 1.03.08 2.07-.52 2.71-1.28z"/></svg>
                Continue with Apple
              </Button>
            )}

            {showGoogle && (
              <>
                <Button
                  onClick={handleGoogle}
                  disabled={providerBusy}
                  variant="outline"
                  className="w-full border-white/15 bg-white/5 hover:bg-white/10 text-slate-200 rounded-full py-5 gap-2"
                  data-testid="google-login-button"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Continue with Google
                </Button>
              </>
            )}

            {providerError && (
              <p className="text-xs text-rose-400 text-center mt-2" data-testid="provider-auth-error">{providerError}</p>
            )}

            <p className="text-[10px] text-slate-600 text-center mt-5">
              By continuing you agree to our{" "}
              <a href="/terms" className="text-amber-500 hover:text-amber-400 underline-offset-2 hover:underline">Terms</a>
              {" "}&{" "}
              <a href="/privacy" className="text-amber-500 hover:text-amber-400 underline-offset-2 hover:underline">Privacy</a>.
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="px-6 py-8 text-center border-t border-white/5">
        <p className="text-slate-600 text-xs">Tara Kundli | Vedic Astrology Consultation</p>
        <p className="text-slate-700 text-[10px] mt-2">
          <a href="/privacy" className="hover:text-amber-400">Privacy</a>
          <span className="mx-2">·</span>
          <a href="/terms" className="hover:text-amber-400">Terms</a>
          <span className="mx-2">·</span>
          <a href="mailto:support@tarakundli.com" className="hover:text-amber-400">Support</a>
        </p>
      </footer>
    </div>
  );
}
